import { motion, useReducedMotion, type Variants } from "framer-motion";

/**
 * 2-of-3 multisig, shown rather than described. The combinatorial rule, "any
 * two of three, but never one", is the second place animation genuinely beats
 * copy, because it's about a threshold you have to watch cross.
 *
 * Beats (scroll-triggered, once): three keys + a locked padlock fade in ->
 * one key alone tries and the lock shakes but stays shut -> two of the three
 * keys light up and the padlock opens. The final frame is the "unlocked by two"
 * end state, so reduced-motion users see that directly.
 *
 * Fixed viewBox => zero layout shift. framer-motion is already a dependency.
 */

const TEAL = "#2dd4bf";
const AMBER = "#fbbf24";
const SLATE = "#94a3b8";
const INK = "#0f172a";

/** Vertical key with the bow at (x, y). */
function KeyDown({ x, y }: { x: number; y: number }) {
  return (
    <g transform={`translate(${x} ${y})`} stroke={TEAL} strokeWidth={3} fill="none" strokeLinecap="round">
      <circle cx={0} cy={0} r={8} />
      <line x1={0} y1={8} x2={0} y2={36} />
      <line x1={0} y1={26} x2={7} y2={26} />
      <line x1={0} y1={32} x2={5} y2={32} />
    </g>
  );
}

export default function MultisigThresholdDiagram() {
  const reduce = useReducedMotion();

  const fade: Variants = {
    hidden: { opacity: 0, y: 8 },
    show: (i: number) => ({ opacity: 1, y: 0, transition: { duration: reduce ? 0 : 0.5, delay: reduce ? 0 : i } }),
  };
  // One key alone: amber halo pulses on, then off.
  const soloHalo: Variants = {
    hidden: { opacity: 0 },
    show: reduce ? { opacity: 0 } : { opacity: [0, 0.5, 0.5, 0], transition: { duration: 1.4, delay: 1.0, times: [0, 0.2, 0.75, 1] } },
  };
  // The lock refuses: a short shake.
  const shake: Variants = {
    hidden: { x: 0 },
    show: reduce ? { x: 0 } : { x: [0, -5, 5, -4, 4, 0], transition: { duration: 0.5, delay: 1.4 } },
  };
  const failMark: Variants = {
    hidden: { opacity: 0 },
    show: reduce ? { opacity: 0 } : { opacity: [0, 1, 1, 0], scale: [0.6, 1, 1, 1], transition: { duration: 1.2, delay: 1.4, times: [0, 0.15, 0.7, 1] } },
  };
  // Two keys light up and stay lit.
  const winHalo: Variants = {
    hidden: { opacity: 0 },
    show: reduce ? { opacity: 0.55 } : { opacity: [0, 0.55, 0.55], transition: { duration: 0.8, delay: 2.5 } },
  };
  // The shackle lifts out of the body.
  const shackle: Variants = {
    hidden: { y: 0 },
    show: reduce ? { y: -24 } : { y: [0, 0, -24], transition: { duration: 3.6, delay: 0, times: [0, 0.8, 0.92], ease: "easeOut" } },
  };
  const bodyGlow: Variants = {
    hidden: { opacity: 0 },
    show: reduce ? { opacity: 0.5 } : { opacity: [0, 0, 0.5], transition: { duration: 3.6, times: [0, 0.82, 0.95] } },
  };
  const winMark: Variants = {
    hidden: { opacity: 0 },
    show: reduce ? { opacity: 1 } : { opacity: [0, 0, 1], scale: [0.6, 0.6, 1], transition: { duration: 3.6, times: [0, 0.86, 1] } },
  };

  const keyXs = { you: 250, family: 400, solicitor: 550 };

  return (
    <figure className="my-8">
      <div className="mx-auto w-full max-w-2xl">
        <motion.svg
          viewBox="0 0 800 400"
          className="w-full h-auto"
          style={{ aspectRatio: "800 / 400" }}
          role="img"
          aria-labelledby="ms-title ms-desc"
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.4 }}
        >
          <title id="ms-title">Two-of-three multisig</title>
          <desc id="ms-desc">
            Three keys are held by you, a family member, and your solicitor. Any two of them together can move funds; one key
            alone never can, so no single person, and no single stolen key, can act.
          </desc>

          {/* Key labels */}
          <motion.text variants={fade} custom={0} x={keyXs.you} y={54} textAnchor="middle" fill="#f8fafc" fontSize={18} fontWeight={600}>You</motion.text>
          <motion.text variants={fade} custom={0.1} x={keyXs.family} y={54} textAnchor="middle" fill="#f8fafc" fontSize={18} fontWeight={600}>Family member</motion.text>
          <motion.text variants={fade} custom={0.2} x={keyXs.solicitor} y={54} textAnchor="middle" fill="#f8fafc" fontSize={18} fontWeight={600}>Solicitor</motion.text>

          {/* Halos (behind keys) */}
          <motion.circle variants={soloHalo} cx={keyXs.you} cy={90} r={26} fill={AMBER} />
          <motion.circle variants={winHalo} cx={keyXs.you} cy={90} r={26} fill={TEAL} />
          <motion.circle variants={winHalo} cx={keyXs.solicitor} cy={90} r={26} fill={TEAL} />

          {/* Keys */}
          <motion.g variants={fade} custom={0.5}><KeyDown x={keyXs.you} y={78} /></motion.g>
          <motion.g variants={fade} custom={0.6}><KeyDown x={keyXs.family} y={78} /></motion.g>
          <motion.g variants={fade} custom={0.7}><KeyDown x={keyXs.solicitor} y={78} /></motion.g>

          {/* Padlock (shakes as a whole) */}
          <motion.g variants={shake}>
            {/* success glow behind body */}
            <motion.rect variants={bodyGlow} x={340} y={218} width={120} height={104} rx={20} fill={TEAL} />
            {/* shackle (lifts out) */}
            <motion.path variants={shackle} d="M372 220 V196 a28 28 0 0 1 56 0 V220"
              fill="none" stroke={SLATE} strokeWidth={11} strokeLinecap="round" />
            {/* body */}
            <rect x={352} y={222} width={96} height={92} rx={13} fill={INK} stroke={TEAL} strokeWidth={2.5} />
            {/* keyhole */}
            <circle cx={400} cy={258} r={9} fill="none" stroke={TEAL} strokeWidth={3} />
            <path d="M400 264 L394 286 H406 Z" fill={TEAL} />
          </motion.g>

          {/* Fail marker */}
          <motion.g variants={failMark}>
            <circle cx={520} cy={268} r={17} fill="rgba(248,113,113,0.12)" stroke="#f87171" strokeWidth={2.5} />
            <path d="M513 261 l14 14 M527 261 l-14 14" stroke="#f87171" strokeWidth={3} strokeLinecap="round" />
          </motion.g>
          {/* Success marker */}
          <motion.g variants={winMark}>
            <circle cx={520} cy={268} r={17} fill="rgba(45,212,191,0.12)" stroke={TEAL} strokeWidth={2.5} />
            <path d="M512 268 l5 6 l11 -13" fill="none" stroke={TEAL} strokeWidth={3} strokeLinecap="round" strokeLinejoin="round" />
          </motion.g>

          {/* Threshold caption */}
          <motion.text variants={fade} custom={0.9} x={400} y={362} textAnchor="middle" fill={SLATE} fontSize={17}>
            Any <tspan fill="#f8fafc" fontWeight={700}>two of three</tspan> keys move funds, one alone never can.
          </motion.text>
        </motion.svg>
      </div>
      <figcaption className="mt-3 text-center text-sm text-slate-400 max-w-2xl mx-auto">
        A 2-of-3 setup means no single person, and no single lost or stolen key, can move your bitcoin,
        while any two of the three can still recover it.
      </figcaption>
    </figure>
  );
}
